import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { motion, useScroll, useSpring } from "framer-motion";
import { BTN_PRIMARY } from "@/lib/site";
import { track } from "@/lib/track";

const LINKS = [
    { label: "Services", href: "#services", id: "services" },
    { label: "Our Work", href: "#work", id: "work" },
    { label: "Solutions", href: "#solutions", id: "solutions" },
    { label: "Process", href: "#process", id: "process" },
    { label: "Contact", href: "#contact", id: "contact" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [solid, setSolid] = useState(false);
    const [active, setActive] = useState("");
    const { scrollYProgress } = useScroll();
    const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 24, mass: 0.3 });

    useEffect(() => {
        const onScroll = () => setSolid(window.scrollY > 80);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) setActive(e.target.id);
                });
            },
            { rootMargin: "-45% 0px -50% 0px" }
        );
        LINKS.forEach((l) => {
            const el = document.getElementById(l.id);
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, []);

    const linkTone = solid
        ? "text-[#57504A] hover:text-[#1C1917]"
        : "text-white/70 hover:text-white";

    return (
        <header
            className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,box-shadow] duration-500 ${
                solid
                    ? "border-b border-[#EDE3DA] bg-[#FDF7F2]/85 shadow-[0_6px_24px_-12px_rgb(28,25,23,0.3)] backdrop-blur-xl"
                    : "border-b border-transparent bg-transparent"
            }`}
            data-testid="site-navbar"
        >
            <motion.div
                className="progress-bar absolute inset-x-0 bottom-0 h-[2px]"
                style={{ scaleX: progress }}
                aria-hidden="true"
            />
            <nav
                className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 lg:px-8"
                aria-label="Main navigation"
            >
                <a href="#top" data-testid="nav-brand" aria-label="StackDino — back to top">
                    <img
                        src={solid ? "/assets/stackdino-logo-alpha.png" : "/assets/stackdino-logo-light.png"}
                        alt="StackDino — AI Consultancy"
                        width={1376}
                        height={402}
                        className="h-8 w-auto sm:h-9"
                    />
                </a>

                <div className="hidden items-center gap-7 md:flex">
                    {LINKS.map((l) => (
                        <a
                            key={l.id}
                            href={l.href}
                            data-active={active === l.id}
                            data-testid={`nav-${l.id}-link`}
                            className={`nav-link text-sm font-medium transition-colors duration-300 ${linkTone}`}
                        >
                            {l.label}
                        </a>
                    ))}
                    <a
                        href="#contact"
                        data-testid="nav-discuss-project-btn"
                        onClick={() => track("cta_click", { cta: "nav_discuss_project" })}
                        className={BTN_PRIMARY}
                    >
                        Discuss Your Project
                    </a>
                </div>

                <button
                    type="button"
                    data-testid="nav-mobile-menu-btn"
                    className={`inline-flex items-center justify-center rounded-lg p-2 transition-colors duration-300 md:hidden ${
                        solid || open ? "text-[#1C1917] hover:bg-[#F7F0EA]" : "text-white"
                    }`}
                    aria-expanded={open}
                    aria-label={open ? "Close menu" : "Open menu"}
                    onClick={() => setOpen(!open)}
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </nav>

            {open && (
                <div
                    data-testid="nav-mobile-menu"
                    className="border-t border-[#EDE3DA] bg-[#FDF7F2] px-6 pb-6 pt-2 md:hidden"
                >
                    {LINKS.map((l) => (
                        <a
                            key={l.id}
                            href={l.href}
                            data-testid={`nav-mobile-${l.id}-link`}
                            onClick={() => setOpen(false)}
                            className="block border-b border-[#F7F0EA] py-3 text-base font-medium text-[#1C1917]"
                        >
                            {l.label}
                        </a>
                    ))}
                    <a
                        href="#contact"
                        data-testid="nav-mobile-discuss-project-btn"
                        onClick={() => {
                            setOpen(false);
                            track("cta_click", { cta: "nav_mobile_discuss_project" });
                        }}
                        className={`${BTN_PRIMARY} mt-4 w-full`}
                    >
                        Discuss Your Project
                    </a>
                </div>
            )}
        </header>
    );
}
